import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * SubscriptionGuard component that ensures users have an active subscription
 * before accessing protected routes. Redirects to pricing page if no subscription.
 */
export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ 
  children, 
  fallback 
}) => {
  const location = useLocation();
  const subscriptionStatus = useSubscriptionStatus();

  // Show loading state while checking subscription
  if (subscriptionStatus.loading) {
    return fallback || (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking subscription status...
        </Typography>
      </Box>
    );
  }

  // Show error state if there was an error checking subscription
  if (subscriptionStatus.error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Subscription Check Failed
          </Typography>
          <Typography variant="body2">
            {subscriptionStatus.error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please refresh the page or contact support if the issue persists.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // If user has an active subscription, render children
  if (subscriptionStatus.isActive) {
    return <>{children}</>;
  }

  // If user has a subscription but it needs payment (past due, etc.)
  // Show a banner/alert instead of redirecting
  if (subscriptionStatus.hasSubscription && subscriptionStatus.needsPayment) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Payment Required
          </Typography>
          <Typography variant="body2">
            Your subscription needs attention. Please update your payment method to continue using all features.
          </Typography>
          <Button 
            size="small" 
            variant="contained" 
            color="warning" 
            sx={{ mt: 1 }}
            href="/membership"
          >
            Update Payment
          </Button>
        </Alert>
        {children}
      </Box>
    );
  }

  // If user has no subscription, check if they're a new user or existing customer
  if (!subscriptionStatus.hasSubscription) {
    // For new prospects: redirect to membership page to force subscription
    if (subscriptionStatus.isNewProspect) {
      // Redirect new prospects to membership page - they must subscribe to continue
      return <Navigate to="/membership" replace />;
    }
    
    // For existing customers who had subscriptions but are now inactive:
    // Allow full dashboard access without banners (temporary fix for data linking issues)
    return <>{children}</>;
  }

  // Default fallback - should not reach here, but render children just in case
  return <>{children}</>;
};

/**
 * Higher-order component version of SubscriptionGuard
 */
export const withSubscriptionGuard = <P extends object>(
  Component: React.ComponentType<P>
) => {
  return (props: P) => (
    <SubscriptionGuard>
      <Component {...props} />
    </SubscriptionGuard>
  );
}; 